import type { MouseEventHandler } from 'react'

import { StoreOfferProduct } from '@common'
import Image from 'next/image'

import { useTranslatable } from '@/localization/useTranslatable'

import { brawlHyperChargeAsset } from '../../brawlstars/bundle/helpers'
import { getBrawlSpecialOfferType } from '../../productFilters'
import { DisplayAsset } from '../assetRendering/DisplayAsset'
import { BrawlCardProduct } from '../card/BrawlCardProduct'
import { CardOfferFooter } from '../card/CardOfferFooter'
import { ProductRibbon } from '../cardRibbon/ProductRibbon'
import { OfferValueBadge } from '../offerValueBadge/OfferValueBadge'
import { SpecialOfferRibbon } from '../specialOfferRibbon/SpecialOfferRibbon'
import { StoreTypography } from '../typography/StoreTypography'

import HeroBackground from 'assets/images/components/BrawlerCard/brawlercard-hero-bg.png'

import styles from './BrawlerCard.module.scss'

const Footer = ({ product }: { product: StoreOfferProduct }) => {
  return (
    <div className={styles.Footer}>
      <CardOfferFooter product={product} offerType="bundle" />
    </div>
  )
}

const Items = ({ contents }: { contents: StoreOfferProduct['contents'] }) => {
  const translatable = useTranslatable()
  return (
    <div className={styles.Items}>
      {contents.map((c) => (
        <BrawlCardProduct
          key={c.product.id}
          size="size-bundle-lg"
          game="brawlstars"
          content={c}
          title={translatable(c.product.title, 'title')}
          isHypercharge={brawlHyperChargeAsset(c.product.id)}
          isBrawlerOrSkin={c.product.itemType === 'Hero' || c.product.itemType === 'Skin'}
        />
      ))}
    </div>
  )
}

const Hero = ({ product }: { product: StoreOfferProduct }) => {
  const translatable = useTranslatable()
  const item = product.contents.find((c) => c.product.itemType === 'Hero' || c.product.itemType === 'Skin')

  if (!item) {
    throw new Error('Invalid itemType')
  }

  return (
    <div className={styles.Hero}>
      <Image className={styles.Background} src={HeroBackground} alt="" fill />
      <div className={styles.Character}>
        <DisplayAsset content={item} preferAnimated gameId="brawlstars" />
      </div>
      <StoreTypography className={styles.Title} tag="p" textSize="md" style="outlined" lineHeight="tight">
        {translatable(product.title)}
      </StoreTypography>
    </div>
  )
}

type BrawlerCardProps = {
  product: StoreOfferProduct
  openProductModal: MouseEventHandler<HTMLElement>
}

export const BrawlerCard = ({ product, openProductModal }: BrawlerCardProps) => {
  const specialOfferType = getBrawlSpecialOfferType(product)

  return (
    <div className={styles.BrawlerCard} onClick={openProductModal} role="button">
      <ProductRibbon product={product} position="s" />
      {specialOfferType ? (
        <SpecialOfferRibbon product={product} type={specialOfferType} />
      ) : (
        <OfferValueBadge offerProduct={product} game="brawlstars" />
      )}
      <Hero product={product} />
      <Items contents={product.contents} />
      <Footer product={product} />
    </div>
  )
}
